import { PDFDocument, degrees } from 'pdf-lib';

// export const SERVER = 'http://127.0.0.1:5555';
export const SERVER = 'https://server.inventoryfalcon.com';

export async function sha256(message) {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

export const getCostPerItemId = (itemId) => {
  try {
    const storedCosts = localStorage.getItem('IFCostPerId');
    const parsedCosts = storedCosts ? JSON.parse(storedCosts) : {};
    return parsedCosts[itemId] !== undefined ? parsedCosts[itemId] : undefined;
  } catch (error) {
    console.error("Error retrieving cost:", error);
    return undefined;
  }
};

export const setCostPerItemId = (itemId, cost) => {
  try {
    const storedCosts = localStorage.getItem('IFCostPerId');
    const parsedCosts = storedCosts ? JSON.parse(storedCosts) : {};
    parsedCosts[itemId] = cost;  // Correctly updating the object
    localStorage.setItem("IFCostPerId", JSON.stringify(parsedCosts));
  } catch (error) {
    console.error("Error setting cost:", error);
  }
};

export const exportToCSV = (headers, objects, filename = 'data.csv') => {
  if (objects.length === 0) {
    alert('No items to export');
    return;
  }

  const rows = objects.map(obj => {
    return headers.map(header => obj[[header]].toString().replaceAll(',', '')).join(',') + ",";
  });

  const csvContent = [headers.join(','), ...rows].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);

  link.click();
  URL.revokeObjectURL(url);
};

export const extractDateRange = (data, key) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return null;
  }

  const validRecords = data
    .filter(record => record[key] && typeof record[key] === 'number')
    .sort((a, b) => a[key] - b[key]);

  if (validRecords.length === 0) {
    return null;
  }

  const startDate = new Date(validRecords[0][key]).toISOString().split('T')[0];
  const endDate = new Date(validRecords[validRecords.length - 1][key]).toISOString().split('T')[0];

  return {
    startDate,
    endDate
  };
};

export const formatDate = (date) => {
  const pad = (n) => (n < 10 ? '0' + n : n);
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const yyyy = date.getFullYear();
  const hh = pad(date.getHours());
  const min = pad(date.getMinutes());
  const ss = pad(date.getSeconds());
  return `${mm}-${dd}-${yyyy}-${hh}-${min}-${ss}`;
};

export const generateMultipleBarcodes = async (barcodes, size, direction = "horizontal", expirationDate = undefined) => {
  if (!["US_Letter", "1x2", "A4"].includes(size)) {
    return;
  }

  const url = `${SERVER}/generate_barcode_multiple`;
  const body = {
    barcodes: barcodes,
    size: size
  };
  if (expirationDate) {
    body.expirationDate = expirationDate;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Network error! Status: ${response.status}`);
    }

    const pdfBytes = await response.arrayBuffer();
    const pdfDoc = await PDFDocument.load(pdfBytes);

    const inch = 72.0;
    const cm = inch / 2.54;
    const mm = cm * 0.1;

    const dimensions = {
      "1x2": { width: 144, height: 72 },
      "A4": { width: 210 * mm, height: 297 * mm },
      "US_Letter": { width: 2.615 * inch, height: 1 * inch },
    };
    const pageSize = dimensions[size] || { width: 144, height: 72 };

    let width = Number(pageSize?.width);
    let height = Number(pageSize?.height);

    const pages = pdfDoc.getPages();

    for (const page of pages) {
      page.setSize(width, height);

      if (["1x2", "US_Letter"].includes(size) && direction === "vertical") {
        page.setRotation(degrees(90));
      }
    }

    const modifiedPdfBytes = await pdfDoc.save();
    const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);

    const printWindow = window.open(blobUrl);
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }

  } catch (error) {
    console.error(`Generate Code request failed:`, error.message);
  }
}

export const generateBarcode = async (barcode, title, size = "1x2", direction = "horizontal", expirationDate = undefined) => {
  if (!["US_Letter", "1x2", "A4"].includes(size)) {
    return;
  }
  const url = `${SERVER}/generate_barcode`;
  const body = {
    barcode: barcode,
    name: title,
    size: size
  };
  if (expirationDate) {
    body.expirationDate = expirationDate;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Network error! Status: ${response.status}`);
    }

    const pdfBytes = await response.arrayBuffer();
    const pdfDoc = await PDFDocument.load(pdfBytes);

    const inch = 72.0;
    const cm = inch / 2.54;
    const mm = cm * 0.1;

    const dimensions = {
      "1x2": { width: 144, height: 72 },
      "A4": { width: 210 * mm, height: 297 * mm },
      "US_Letter": { width: 2.615 * inch, height: 1 * inch },
    };
    const pageSize = dimensions[size] || { width: 144, height: 72 };

    let width = Number(pageSize?.width);
    let height = Number(pageSize?.height);

    // Resize page correctly
    const pages = pdfDoc.getPages();
    const page = pages[0];
    page.setSize(width, height);

    // Rotate the page if direction is vertical
    if (["1x2", "US_Letter"].includes(size) && direction === "vertical") {
      page.setRotation(degrees(90)); // Add 90 degrees to the current rotation
    }

    const modifiedPdfBytes = await pdfDoc.save();
    const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);

    // Open in new tab for printing
    const printWindow = window.open(blobUrl);
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }

  } catch (error) {
    console.error(`Generate Code request failed:`, error.message);
  }
};

export const mergeObjects = (obj1, obj2) => {
  return Object.fromEntries(
    Object.entries({ ...obj1, ...obj2 }).map(([key, value]) => {
      if (obj2[key] === null || obj2[key] === "") return [key, obj1[key]]; // Keep obj1 value if obj2 is null
      if (obj1[key] === null || obj1[key] === "") return [key, obj2[key]]; // Take from obj2 if obj1 is null

      // Special handling for arrays
      if (Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
        return [key, obj2[key]];
      }

      // Handle case where one is array and one is not
      if (Array.isArray(obj1[key]) && !Array.isArray(obj2[key])) {
        // If obj2 has a single value and obj1 is an array, keep the array
        return [key, obj1[key]];
      }

      if (!Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
        // If obj1 has a single value and obj2 is an array, use the array
        return [key, obj2[key]];
      }

      // Deep merge for objects (non-arrays)
      if (typeof obj1[key] === "object" && typeof obj2[key] === "object" &&
        obj1[key] !== null && obj2[key] !== null &&
        !Array.isArray(obj1[key]) && !Array.isArray(obj2[key])) {
        return [key, mergeObjects(obj1[key], obj2[key])];
      }

      // Keep obj1[key] if obj2[key] is 0
      if (value === 0 && obj1[key] !== undefined) return [key, obj1[key]];

      return [key, value];
    })
  );
};

export const convertsMass = (weight, unitFrom, unitTo) => {
  let factorFrom = 1;
  let factorTo = 1;

  if (unitFrom === "lbs.oz") {
    const [lbs, oz] = weight.toString().split(".");
    weight = parseInt(lbs) + parseInt(oz) * (1 / 16);
    unitFrom = "lbs";
  }


  if (unitFrom.toLowerCase().includes("ounce")) {
    factorFrom = 1 / 16;
  } else if (unitFrom.toLowerCase().includes("gram")) {
    factorFrom = 1 / 453.592;
  } else if (unitFrom.toLowerCase().includes("kilogram") || unitFrom.toLowerCase().includes("kg")) {
    factorFrom = 2.20462;
  } else if (unitFrom.toLowerCase().includes("milligram") || unitFrom.toLowerCase().includes("mg")) {
    factorFrom = 1 / 453592;
  } else if (unitFrom.toLowerCase().includes("lb") || unitFrom.toLowerCase().includes("lbs")) {
    factorFrom = 1;
  }

  if (unitTo.toLowerCase().includes("ounce")) {
    factorTo = 16;
  } else if (unitTo.toLowerCase().includes("gram")) {
    factorTo = 453.592;
  } else if (unitTo.toLowerCase().includes("kilogram") || unitTo.toLowerCase().includes("kg")) {
    factorTo = 1 / 2.20462;
  } else if (unitTo.toLowerCase().includes("milligram") || unitTo.toLowerCase().includes("mg")) {
    factorTo = 453592;
  } else if (unitTo.toLowerCase().includes("lb") || unitTo.toLowerCase().includes("lbs")) {
    factorTo = 1;
  } else if (unitTo.toLowerCase().includes("lbs.oz")) {
    factorTo = 1;
  }

  return weight * factorFrom * factorTo;
};

export const convertsLength = (length, unitFrom, unitTo) => {
  let factorFrom = 1;
  let factorTo = 1;

  if (unitFrom.toLowerCase().includes("centimeter") || unitFrom.toLowerCase().includes("cm")) {
    factorFrom = 1 / 2.54;
  } else if (unitFrom.toLowerCase().includes("meter")) {
    factorFrom = 39.3701;
  } else if (unitFrom.toLowerCase().includes("millimeter") || unitFrom.toLowerCase().includes("mm")) {
    factorFrom = 1 / 25.4;
  } else if (unitFrom.toLowerCase().includes("inch") || unitFrom.toLowerCase().includes("inches")) {
    factorFrom = 1;
  } else if (unitFrom.toLowerCase().includes("foot") || unitFrom.toLowerCase().includes("feet")) {
    factorFrom = 12;
  } else if (unitFrom.toLowerCase().includes("yard") || unitFrom.toLowerCase().includes("yd")) {
    factorFrom = 36;
  }

  if (unitTo.toLowerCase().includes("centimeter") || unitTo.toLowerCase().includes("cm")) {
    factorTo = 2.54;
  } else if (unitTo.toLowerCase().includes("meter")) {
    factorTo = 1 / 39.3701;
  } else if (unitTo.toLowerCase().includes("millimeter") || unitTo.toLowerCase().includes("mm")) {
    factorTo = 25.4;
  } else if (unitTo.toLowerCase().includes("inch") || unitTo.toLowerCase().includes("inches")) {
    factorTo = 1;
  } else if (unitTo.toLowerCase().includes("foot") || unitTo.toLowerCase().includes("feet")) {
    factorTo = 1 / 12;
  } else if (unitTo.toLowerCase().includes("yard") || unitTo.toLowerCase().includes("yd")) {
    factorTo = 1 / 36;
  }

  return length * factorFrom * factorTo;
};

export const float2Decimal = (number) => {
  const numberFloat = parseFloat(number);
  return parseFloat(numberFloat.toFixed(2));
}

export function calcWfsCost(dimensions, category, priceGuess, antimicrobial, weight) {
  let wfs = 0;

  if (!antimicrobial) {
    if (dimensions) {

      wfs = calcWfs({
        ...dimensions,
        weight
      });

      if (wfs !== 0) {
        if (priceGuess < 10) wfs += 1;
        if (category === "Clothing") wfs += 0.5;
      }
    }
  }
  return wfs;
}

export function calcBreakeven(dimensions, category, shelf, costPrice, otherFees, antimicrobial, weight) {
  let S = costPrice + otherFees;
  let tolerance = 0.0001;
  let maxIterations = 40;
  let iteration = 0;

  while (iteration < maxIterations) {
    iteration++;
    const wfsCost = calcWfsCost(dimensions, category, S, antimicrobial, weight);
    const refFees = calcRefFees({
      shelf,
      price: {
        amount: S,
      },
    });
    const totalCost = costPrice + otherFees + refFees + wfsCost;

    if (Math.abs(totalCost - S) < tolerance) {
      return parseFloat(S);
    }

    S = totalCost;
  }

  return parseFloat(S);
}

export const calcWfs = (item) => {
  const dimWeight = (item.height * item.length * item.width) / 139;
  if (0 < item.weight && item.weight < 0.75) {
    return 3.45;
  }
  if (0.75 <= item.weight && item.weight < 1) {
    return 4.95;
  }

  const shippingWeight = Math.ceil(Math.max(item.weight, dimWeight) + 0.25);
  if (shippingWeight <= 1) {
    return 3.45;
  }
  else if (shippingWeight === 2) {
    return 4.95;
  }
  else if (shippingWeight === 3) {
    return 5.45;
  }
  else if (4 <= shippingWeight <= 20) {
    const additionalWeight = shippingWeight - 4;
    return 5.75 + (0.4 * additionalWeight);
  }
  else if (21 <= shippingWeight <= 30) {
    const additionalWeight = shippingWeight - 21;
    return 15.55 + (0.4 * additionalWeight);
  }
  else if (31 <= shippingWeight <= 50) {
    const additionalWeight = shippingWeight - 31;
    return 14.55 + (0.4 * additionalWeight);
  }
  else if (shippingWeight >= 51) {
    const additionalWeight = shippingWeight - 51;
    return 17.55 + (0.4 * additionalWeight);
  }
};

export const calcRefFees = (item) => {
  const price = item.price?.amount ?? 0;
  const shelf = JSON.parse(item.shelf || "[]");

  if (shelf.length === 0) {
    return price * 0.15;
  }

  let [first, second = "", third = ""] = shelf;
  let category = "";
  let subCat = "";

  if (first === "Home Page") {
    category = second;
    subCat = third;
  } else {
    category = first;
    subCat = second;
  }

  if (category === "Clothing") {
    if (price <= 15) {
      return price * 0.05
    }
    if (price <= 20) {
      return price * 0.1
    }
  }
  if (subCat === "Auto Electronics" || category === "Cell Phones" || subCat.includes("Cameras")) {
    return price * 0.08
  }
  if (category === "Auto & Tires" || category === "Musical Instruments") {
    if (subCat.includes("Tires")) {
      return price * 0.1
    }
    return price * 0.12
  }
  if (category === "Baby" || category === "Beauty" || category === "Personal Care" || category.includes("Health")) {
    if (price <= 10) {
      return price * 0.08
    }
  }
  if (category === "Food" || category === "Grocery") {
    if (price <= 15) {
      return price * 0.08
    }
  }
  if (category === "Electronics" && price > 100) {
    return price * 0.08
  }
  if (category === "Jewelry") {
    return price * 0.2
  }
  if (subCat.includes("Computers")) {
    return price * 0.06
  }

  return price * 0.15;
};

export const addToSentItemsLS = (subUser, item) => {
  const sentItem = {
    sku: item.sku,
    title: item.productName || item.title,
    image: item.image,
    qty: parseInt(item.qty),
    unpaidQty: parseInt(item.qty),
    cost: item.cost,
    date: new Date().toISOString(),
  };

  const sentItemsLS = JSON.parse(localStorage.getItem("IFSentItemsSU") || '{}');

  if (!sentItemsLS[subUser]) {
    sentItemsLS[subUser] = [];
  }

  sentItemsLS[subUser].push(sentItem);
  localStorage.setItem("IFSentItemsSU", JSON.stringify(sentItemsLS));
};

export const addToSentItemsLSBulk = (subUser, items) => {
  const sentItems = items.map(item => {
    return {
      sku: item.sku,
      title: item.productName || item.title,
      image: item.image,
      qty: parseInt(item.qty),
      unpaidQty: parseInt(item.qty),
      cost: item.cost,
      date: new Date().toISOString(),
    }
  });

  const sentItemsLS = JSON.parse(localStorage.getItem("IFSentItemsSU") || '{}');

  if (!sentItemsLS[subUser]) {
    sentItemsLS[subUser] = [];
  }

  sentItemsLS[subUser].concat(sentItems);
  localStorage.setItem("IFSentItemsSU", JSON.stringify(sentItemsLS));
};

export const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// export const addItemsToInventory = async (items, user, password, encodedAuth) => {
//   const body = {
//     items: items
//   };
//   const url = `${SERVER}/post_bulk_item_inventory_update`;
//   try {
//     const response = fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body: body });
//     // TODO: check feedId that everything is ok => alert GOOD response if ok, else alert BAD + error details


//     // TODO: reset addedItem array

//     return true;
//   } catch (error) {
//     return false;
//   }
// };

export const sendErrorMessage = async (email, password, channel, itemId, barcode, page, step, func, status, errors, reqBody = {}) => {
  const body = {
    email: email,
    channel: channel,
    itemId: itemId,
    barcode: barcode,
    page: page,
    step: step,
    func: func,
    status: status,
    errors: errors,
    body: reqBody
  };

  try {
    fetchWrapperNoAuth(`${SERVER}/send_error_msg`, email, password, { method: 'POST', body: body })
  } catch (error) {
    console.error(error);
    return;
  }
};

export const fetchNextDataWithRetry = async (email, password, itemId, maxRetries = 5, retryDelay = 1000) => {
  let attempt = 0;

  while (attempt < maxRetries) {
    try {
      let nextData = await fetchWrapperNoAuth(`${SERVER}/get_item_nextdata/${itemId}`, email, password);
      if (!nextData.success) {
        console.log(`Attempt ${attempt + 1} failed`);
      } else {
        return nextData;
      }
    } catch (error) {
      console.log(`Attempt ${attempt + 1} encountered an error: ${error.message}`);
    }

    attempt++;
    if (attempt < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
  }

  return {
    length: 0.0,
    width: 0.0,
    height: 0.0,
    dimensionsUnit: "Inches",
    weight: 0.0,
    weightUnit: "lbs",
    manufacturer: "",
    category: null,
    price: {
      amount: 0,
      currency: 'USD'
    },
    upc: "",
    brand: "",
    images: [],
    shelf: "[]",
    badges: [],
    offerCount: 0
  }
};

export const fetchWrapper = async (url, email, password, encodedAuth, options = {}) => {
  const { method = 'GET', body, } = options;

  const payload = {
    mode: 'cors',
    method: method,
    headers: {
      'Accept': 'application/json',
      'IFawa': encodedAuth,
      'IFydm': email,
      'IFwyc': password,
      'Content-Type': 'application/json',
    },
  }

  if (body) {
    payload.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, payload);
    if (!response.ok) {
      throw new Error(`Network error! Status: ${response.status} - ${response.statusText}`);
    }

    const responseData = await response.json();
    if (!responseData.success) {
      throw new Error(responseData.error);
    }
    return responseData.data;

  } catch (error) {
    throw error; // Re-throw errors
  }
};

export const fetchWrapperShipments = async (url, email, password, encodedAuth, options = {}) => {
  const { method = 'POST', body = {}, } = options;

  const payload = {
    mode: 'cors',
    method: method,
    headers: {
      'Accept': 'application/json',
      'IFawa': encodedAuth,
      'IFydm': email,
      'IFwyc': password,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      'authorization': encodedAuth,
      ...body
    })
  }

  try {
    const response = await fetch(url, payload);
    const responseData = await response.json();

    if (!response.ok) {
      throw responseData?.error || new Error(`Network error! Status: ${response.status} - ${response.statusText}`);
    }

    if (!responseData.success) {
      throw responseData.error || new Error("Unknown error from server.");
    }
    return responseData.data;

  } catch (error) {
    throw error; // Re-throw errors
  }
};

export const fetchWrapperPdf = async (url, email, password, encodedAuth = "", options = {}) => {
  const { method = 'GET', body = {}, } = options;

  const payload = {
    mode: 'cors',
    method: method,
    headers: {
      'Accept': 'application/pdf',
      'IFawa': encodedAuth,
      'IFydm': email,
      'IFwyc': password,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      'authorization': encodedAuth,
      ...body
    })
  }

  try {
    const response = await fetch(url, payload);
    if (!response.ok) {
      throw new Error(`Network error! Status: ${response.status} - ${response.statusText}`);
    }

    const responseData = await response.blob();
    return responseData;

  } catch (error) {
    throw error; // Re-throw errors
  }
};

export const fetchWrapperNoAuth = async (url, email, password, options = {}) => {
  const { method = 'GET', body, } = options;

  const payload = {
    mode: 'cors',
    method: method,
    headers: {
      'Accept': 'application/json',
      'IFydm': email,
      'IFwyc': password,
      'Content-Type': 'application/json',
    },
  }

  if (body) {
    payload.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, payload);
    if (!response.ok) {
      throw new Error(`Network error! Status: ${response.status} - ${response.statusText}`);
    }

    const responseData = await response.json();
    if (!responseData.success) {
      throw new Error(`${responseData.error}`);
    }
    return responseData.data;

  } catch (error) {
    throw error; // Re-throw errors
  }
};

export const cleanItemMetadataToPreventQuotaExceeded = (item) => {
  try {
    if (item.variants) {
      const { variants, ...cleanItem } = item;
      return { ...cleanItem, variants: [] };
    }
    return item;
  } catch {
    return item;
  }
};